import { createContext } from 'react';
import noop from 'lodash/noop';

import api from '../api';
import {
    CreateTravelFile,
    CreateManualTravelFile,
    TableTravelFilesBody,
    TravelFile,
    TravelFileService,
    TravelFileServicePassenger,
    CreateManualTravelFileByPNR
} from '../oldModelTypes';
import { ModelOptions } from '../../../types';
import { Passenger } from '../../passengers/types';
import { PnrDetails } from '../../pnrDetails/types';

import {
    QueryGetTourServicePassengersByTravelFile,
    BuiltUpdateTravelFileService,
    BodyAmendTravelFileService,
    BodyAddServiceToTravelFile,
    BodyUpdateRoomsGroupTravelFile,
    BodyUpdateTravelFileRemarks,
    TourServicePassenger,
    QueryGetTourPassengersByTourId,
    BodyQueryTFType
} from './bodyParamsTypes';
interface Context<T> {
    get: (params?: Partial<T>, options?: ModelOptions, updateState?: boolean) => Promise<Array<T>>;
    getOne: (params: Partial<T>, body?: { populateQuery?: Array<any> }, updateState?: boolean) => Promise<T>;
    post: (body: Partial<T>, updateState?: boolean, last?: boolean) => Promise<T>;
    put: (body: Partial<T>, updateState?: boolean) => Promise<T>;
    remove?: (id: string) => Promise<string>;
    softDelete: (body: { _id: string }) => Promise<any>;
    data: T[];
    setData: (array: Array<T>) => void;
    selected: T | null;
    setSelected: (object: T) => void;
    error?: T | null | {};
    selectedPassengersIds: Array<Passenger['_id']> | null;
    setSelectedPassengersIds: (selectedPassengersIds: Array<Passenger['_id']> | null) => void;
    priceExcludedPassengerIdsByTf: Record<string, Array<string | number>>;
    setPriceExcludedPassengerIdsByTf: (next: Record<string, Array<string | number>>) => void;
}

export interface ContextTravelFile extends Context<TravelFile> {
    getTourServicePassengersByTravelFile: (
        query: QueryGetTourServicePassengersByTravelFile
    ) => Promise<Array<TravelFileServicePassenger & { passengerInfo: Passenger }>>;
    createTravelFileByService: (body: CreateTravelFile) => Promise<TravelFile>;
    createTravelFileNoService: (body: CreateManualTravelFile) => Promise<TravelFile>;
    getTravelFile: (query: any, updateState?: boolean) => Promise<TravelFile>;
    getTableTravelFiles: (body: TableTravelFilesBody) => Promise<Array<TravelFile>>;
    getTravelFiles: (query: any) => Promise<Array<TravelFile>>;
    getTravelFilesByIds: (
        ids: Array<string | number>,
        options?: { withUnexportedCards?: boolean }
    ) => Promise<Array<TravelFile>>;
    getLedgerNumbering: () => Promise<{
        ledgerFrom: number | null;
        counter: number | null;
        lastExported: string | null;
        missing: Array<{ refNumber: string; cause: string }>;
    }>;
    markTravelFilesLedgerExported: (ids: Array<string | number>) => Promise<{ updatedIds: Array<number> }>;
    sendTravelFileToCrm: (travelFileId: number | string) => Promise<{ sent: boolean }>;
    getTravelFilesByTicketNumber: (query: any) => Promise<Array<TravelFile>>;
    getTravelFilesByTourDetails: (query: any) => Promise<Array<TravelFile>>;
    getTravelFilesByInventory: (query: any) => Promise<Array<TravelFile>>;
    getTravelFilesByPassengerDetails: (query: any) => Promise<Array<TravelFile>>;
    // Branded: only a payload built by buildUpdateServicePayload is accepted, so
    // no caller can dodge declaring whether it owns the passenger list.
    updateTravelFileService: (body: BuiltUpdateTravelFileService) => Promise<TravelFileService>;
    /**
     * Resolves with the server's warnings (something was NOT done — the price
     * was kept) and notices (done — the price change, documents already issued).
     */
    amendTravelFileService: (
        body: BodyAmendTravelFileService
    ) => Promise<{ warnings: string[]; notices: string[] }>;
    addServiceToTravelFile: (body: BodyAddServiceToTravelFile) => Promise<TravelFileService>;
    updateRoomsGroupTravelFile: (body: BodyUpdateRoomsGroupTravelFile) => Promise<boolean>;
    updateTravelFileRemarks: (body: BodyUpdateTravelFileRemarks) => Promise<boolean>;
    registerPassengersUpdateTourAndTravelFile: (body: any) => Promise<boolean>;
    tourPassengersData: Array<TourServicePassenger>;
    setTourPassengersData: (tourPassengersData: Array<TourServicePassenger>) => void;
    query: (
        body: BodyQueryTFType
    ) => Promise<{ data: TravelFile[]; total: number; totalPages: number; currentPage: number }>;
    getTourPassengersByTourId: (
        query: QueryGetTourPassengersByTourId
    ) => Promise<Array<TravelFileServicePassenger & { passengerInfo: Passenger }>>;
    resetContext: () => void;
    createManualTravelFileByPNR: (body: CreateManualTravelFileByPNR) => Promise<TravelFile & PnrDetails>;
    getGroupTravelFilesByTourDetails: (query: any) => Promise<Array<TravelFile>>;
    getClientTravelFilesByGroupId: (groupId: string) => Promise<Array<TravelFile>>;
    getFullService: (serviceId: string) => Promise<any>;
    closeTravelFile: (body: any) => Promise<any>;
    reopenTravelFile: (body: { travelFileId: string }) => Promise<any>;
    deleteService: (travelFileServiceId: string) => Promise<void>;
    restoreService: (travelFileServiceId: string) => Promise<void>;
    deleteTravelFile: (body: {
        travelFileId: string | number;
        currUserDetails?: { id?: string; name?: string; email?: string };
    }) => Promise<{ _id: string | number; deleted: boolean }>;
    cancelTravelFile: (body: {
        travelFileId: string | number;
    }) => Promise<{ _id: string | number; status: string; updatedAt: string }>;
    undoCancelTravelFile: (body: {
        travelFileId: string | number;
    }) => Promise<{ _id: string | number; status: string; updatedAt: string }>;
    updateTravelAgent: (body: {
        travelFileId: string | number;
        travelAgencyAccountId: string | number | null;
        confirmHasDocuments?: boolean;
    }) => Promise<{
        _id: string | number;
        travelAgencyAccount: number | null;
        travelAgencyAccountName: string | null;
        unchanged?: boolean;
    }>;
    getSalesAndProfit: (query: any) => Promise<any[]>;
    getSalesAndProfitSummary: (query: any) => Promise<any[]>;
    getReservationsReport: (query: any) => Promise<any[]>;
    getCancelledPassengersReport: (query: any) => Promise<any[]>;
    getLeftToPayReport: (query: any) => Promise<any[]>;
}

const TravelFilesContext = createContext<ContextTravelFile>({
    get: api.get,
    getOne: api.getOne,
    post: api.post,
    put: api.put,
    remove: api.remove,
    softDelete: api.softDelete,
    data: [],
    setData: noop,
    selected: null,
    setSelected: noop,
    error: null,
    selectedPassengersIds: null,
    setSelectedPassengersIds: noop,
    priceExcludedPassengerIdsByTf: {},
    setPriceExcludedPassengerIdsByTf: noop,
    getTourServicePassengersByTravelFile: api.getTourServicePassengersByTravelFile,
    tourPassengersData: [],
    setTourPassengersData: noop,
    query: api.query,
    createTravelFileByService: api.createTravelFileByService,
    createTravelFileNoService: api.createTravelFileNoService,
    getTravelFile: api.getTravelFile,
    getTableTravelFiles: api.getTableTravelFiles,
    getTravelFilesByTicketNumber: api.getTravelFilesByTicketNumber,
    getTravelFiles: api.getTravelFiles,
    getTravelFilesByIds: api.getTravelFilesByIds,
    getLedgerNumbering: api.getLedgerNumbering,
    markTravelFilesLedgerExported: api.markTravelFilesLedgerExported,
    sendTravelFileToCrm: api.sendTravelFileToCrm,
    getTravelFilesByTourDetails: api.getTravelFilesByTourDetails,
    getTravelFilesByInventory: api.getTravelFilesByInventory,
    getTravelFilesByPassengerDetails: api.getTravelFilesByPassengerDetails,
    updateTravelFileService: api.updateTravelFileService,
    amendTravelFileService: api.amendTravelFileService,
    addServiceToTravelFile: api.addServiceToTravelFile,
    updateRoomsGroupTravelFile: api.updateRoomsGroupTravelFile,
    updateTravelFileRemarks: api.updateTravelFileRemarks,
    registerPassengersUpdateTourAndTravelFile: api.registerPassengersUpdateTourAndTravelFile,
    getTourPassengersByTourId: api.getTourPassengersByTourId,
    resetContext: noop,
    createManualTravelFileByPNR: api.createManualTravelFileByPNR,
    getGroupTravelFilesByTourDetails: api.getGroupTravelFilesByTourDetails,
    getClientTravelFilesByGroupId: api.getClientTravelFilesByGroupId,
    getFullService: api.getFullService,
    closeTravelFile: api.closeTravelFile,
    reopenTravelFile: api.reopenTravelFile,
    deleteService: () => Promise.resolve(),
    restoreService: () => Promise.resolve(),
    deleteTravelFile: () => Promise.resolve({ _id: '', deleted: false }),
    cancelTravelFile: api.cancelTravelFile,
    undoCancelTravelFile: api.undoCancelTravelFile,
    updateTravelAgent: api.updateTravelAgent,
    getSalesAndProfit: api.getSalesAndProfit,
    getSalesAndProfitSummary: api.getSalesAndProfitSummary,
    getReservationsReport: api.getReservationsReport,
    getCancelledPassengersReport: api.getCancelledPassengersReport,
    getLeftToPayReport: api.getLeftToPayReport
});
export default TravelFilesContext;
